import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import type { JSX } from "react";
import { cn } from "../ui";
import { PaneFrame } from "./PaneBlocks";
import {
  AGENT_PANE_METRICS,
  LIST_PANE_METRICS,
  WORKSPACE_RESIZE_HANDLE_WIDTH,
} from "./layoutMetrics";
import { clampPaneWidth, resolveThreePanelLayout } from "./splitLayout";

const RESIZE_STEP = 24;

type ResizeTarget = "list" | "sidebar";

interface PersistedPaneWidths {
  readonly listWidth?: number;
  readonly sidebarWidth?: number;
}

interface DragState {
  readonly target: ResizeTarget;
  readonly startX: number;
  readonly startWidth: number;
}

export interface ThreePanelProps {
  readonly listPane: ReactNode;
  readonly detailPane: ReactNode;
  readonly sidebarPane?: ReactNode;
  readonly storageKey?: string;
  readonly containerClassName?: string;
  readonly listPaneClassName?: string;
  readonly detailPaneClassName?: string;
  readonly sidebarPaneClassName?: string;
  readonly listHandleBackground?: string;
  readonly sidebarHandleBackground?: string;
}

function readPersistedPaneWidths(storageKey?: string): PersistedPaneWidths | null {
  if (!storageKey || typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      listWidth:
        typeof parsed.listWidth === "number" ? parsed.listWidth : undefined,
      sidebarWidth:
        typeof parsed.sidebarWidth === "number" ? parsed.sidebarWidth : undefined,
    };
  } catch {
    return null;
  }
}

function persistPaneWidths(
  storageKey: string | undefined,
  paneWidths: PersistedPaneWidths,
): void {
  if (!storageKey || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(paneWidths));
  } catch {
    // Ignore storage failures; resize still works in-memory.
  }
}

function PaneResizeHandle({
  label,
  value,
  min,
  max,
  active,
  background,
  onPointerDown,
  onKeyDown,
}: {
  readonly label: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly active: boolean;
  readonly background?: string;
  readonly onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  readonly onKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>) => void;
}): JSX.Element {

  return (
    <button
      type="button"
      role="separator"
      aria-label={label}
      aria-orientation="vertical"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={Math.round(value)}
      data-no-window-drag
      className={cn(
        "workspace-resize-handle group relative h-full shrink-0 touch-none select-none border-none p-0 outline-none",
        active ? "workspace-resize-handle-active" : "",
      )}
      style={{ width: WORKSPACE_RESIZE_HANDLE_WIDTH, background }}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    >
      <span className="workspace-resize-handle-line" />
    </button>
  );
}

/**
 * Shared three-pane layout:
 * - left list pane (bounded, resizable)
 * - center detail pane (dominant, flexible)
 * - optional right sidebar pane (bounded, resizable)
 */
export function ThreePanel({
  listPane,
  detailPane,
  sidebarPane,
  storageKey,
  containerClassName,
  listPaneClassName,
  detailPaneClassName,
  sidebarPaneClassName,
  listHandleBackground,
  sidebarHandleBackground,
}: ThreePanelProps): JSX.Element {
  const hasSidebar = sidebarPane !== undefined && sidebarPane !== null;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [paneWidths, setPaneWidths] = useState(() => {
    const persisted = readPersistedPaneWidths(storageKey);
    return {
      listWidth: clampPaneWidth(
        persisted?.listWidth ?? LIST_PANE_METRICS.defaultWidth,
        LIST_PANE_METRICS,
      ),
      sidebarWidth: clampPaneWidth(
        persisted?.sidebarWidth ?? AGENT_PANE_METRICS.defaultWidth,
        AGENT_PANE_METRICS,
      ),
    };
  });

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const updateWidth = (): void => {
      setContainerWidth(element.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      return () => {
        window.removeEventListener("resize", updateWidth);
      };
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setContainerWidth(entry?.contentRect.width ?? element.clientWidth);
    });

    observer.observe(element);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    persistPaneWidths(storageKey, paneWidths);
  }, [paneWidths, storageKey]);

  useEffect(() => {
    if (!dragState) {
      return;
    }

    const handlePointerMove = (event: PointerEvent): void => {
      event.preventDefault();

      setPaneWidths((current) => {
        if (dragState.target === "list") {
          return {
            ...current,
            listWidth: clampPaneWidth(
              dragState.startWidth + (event.clientX - dragState.startX),
              LIST_PANE_METRICS,
            ),
          };
        }

        return {
          ...current,
          sidebarWidth: clampPaneWidth(
            dragState.startWidth - (event.clientX - dragState.startX),
            AGENT_PANE_METRICS,
          ),
        };
      });
    };

    const stopDragging = (): void => {
      setDragState(null);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopDragging);
    window.addEventListener("pointercancel", stopDragging);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopDragging);
      window.removeEventListener("pointercancel", stopDragging);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [dragState]);

  const layout = useMemo(
    () =>
      resolveThreePanelLayout({
        containerWidth,
        listWidth: paneWidths.listWidth,
        sidebarWidth: paneWidths.sidebarWidth,
        hasSidebar,
      }),
    [containerWidth, hasSidebar, paneWidths.listWidth, paneWidths.sidebarWidth],
  );

  const handleStartDrag =
    (target: ResizeTarget) =>
    (event: ReactPointerEvent<HTMLButtonElement>): void => {
      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      setDragState({
        target,
        startX: event.clientX,
        startWidth:
          target === "list" ? layout.listWidth : layout.sidebarWidth,
      });
    };

  const handleKeyResize =
    (target: ResizeTarget) =>
    (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
      const step =
        event.key === "ArrowLeft"
          ? -RESIZE_STEP
          : event.key === "ArrowRight"
            ? RESIZE_STEP
            : 0;

      if (step === 0) {
        return;
      }

      event.preventDefault();

      setPaneWidths((current) => {
        if (target === "list") {
          return {
            ...current,
            listWidth: clampPaneWidth(
              current.listWidth + step,
              LIST_PANE_METRICS,
            ),
          };
        }

        return {
          ...current,
          sidebarWidth: clampPaneWidth(
            current.sidebarWidth - step,
            AGENT_PANE_METRICS,
          ),
        };
      });
    };

  const gridTemplateColumns = hasSidebar
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    ? `${layout.listWidth}px ${WORKSPACE_RESIZE_HANDLE_WIDTH}px minmax(0, ${layout.detailWidth}px) ${WORKSPACE_RESIZE_HANDLE_WIDTH}px ${layout.sidebarWidth}px`
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    : `${layout.listWidth}px ${WORKSPACE_RESIZE_HANDLE_WIDTH}px minmax(0, ${layout.detailWidth}px)`;

  const gridStyle: CSSProperties = {
    width: layout.totalWidth,
    gridTemplateColumns,
  };

  return (
    <div
      ref={containerRef}
      className={cn("h-full min-h-0 min-w-0 w-full overflow-hidden", containerClassName)}
    >
      <div className="grid h-full min-h-0" style={gridStyle}>
        <PaneFrame
          tone="surface-secondary"
          className={cn("overflow-hidden", listPaneClassName)}
        >
          {listPane}
        </PaneFrame>
        <PaneResizeHandle
          label="Resize list pane"
          value={layout.listWidth}
          min={LIST_PANE_METRICS.minWidth}
          max={LIST_PANE_METRICS.maxWidth}
          active={dragState?.target === "list"}
          background={listHandleBackground}
          onPointerDown={handleStartDrag("list")}
          onKeyDown={handleKeyResize("list")}
        />
        <div
          className={cn(
            "min-h-0 min-w-0 overflow-hidden",
            detailPaneClassName ?? "bg-surface",
          )}
        >
          {detailPane}
        </div>
        {hasSidebar && (
          <PaneResizeHandle
            label="Resize agent pane"
            value={layout.sidebarWidth}
            min={AGENT_PANE_METRICS.minWidth}
            max={AGENT_PANE_METRICS.maxWidth}
            active={dragState?.target === "sidebar"}
            background={sidebarHandleBackground}
            onPointerDown={handleStartDrag("sidebar")}
            onKeyDown={handleKeyResize("sidebar")}
          />
        )}
        {hasSidebar && (
          <PaneFrame
            tone="surface-secondary"
            className={cn("overflow-hidden", sidebarPaneClassName)}
          >
            {sidebarPane}
          </PaneFrame>
        )}
      </div>
    </div>
  );
}
